import React, { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { QuizForm, QuizFormType } from './components/QuizForm';

import { formatDateAsAnniversary } from 'helpers/dateTimes';

import { useCreateQuizMutation } from 'data/__generated__';

export const QuizCreate = () => {
    const navigate = useNavigate();

    const [createQuiz, { loading: creationLoading }] = useCreateQuizMutation();

    const initialForm: QuizFormType = {
        audience: { organizationIds: [], siteIds: [], userIds: [] },
        date: null,
        description: null,
        image: null,
        imageUrl: null,
        questions: [],
        title: '',
    };
    const [quizForm, setQuizForm] = useState<QuizFormType>(initialForm);

    async function onSubmit() {
        if (!quizForm.questions.length) {
            toast.error('Veuillez renseigner au moins une question');
            return;
        }
        if (!quizForm.date) {
            toast.error('Date du quiz non renseignée.');
            return;
        }
        if (!quizForm.description) {
            toast.error('Description non renseignée.');
            return;
        }
        if (!quizForm.title) {
            toast.error('Titre du quiz non renseigné.');
            return;
        }
        if (!quizForm.image && !quizForm.imageUrl) {
            toast.error('Image du quiz non renseignée.');
            return;
        }
        let isQuizQuestionsWrong = false;
        quizForm.questions.forEach(({ answerOptions, correctAnswerIndex, label, image, imageUrl }, questionIndex) => {
            if (
                !label ||
                !answerOptions.length ||
                answerOptions.length <= correctAnswerIndex ||
                (!image && !imageUrl)
            ) {
                isQuizQuestionsWrong = true;
                if (!label) toast.error(`Question ${questionIndex + 1} : veuillez renseigner le texte de la question.`);
                if (!answerOptions.length)
                    toast.error(
                        `Question ${questionIndex + 1} : veuillez renseigner au moins une réponse à la question.`,
                    );
                if (answerOptions.length <= correctAnswerIndex)
                    toast.error(`Question ${questionIndex + 1} : veuillez cocher la réponse correcte.`);
                if (!image && !imageUrl) toast.error(`Question ${questionIndex + 1} : veuillez renseigner une image.`);
            }
        });
        if (isQuizQuestionsWrong) {
            return;
        }
        const { data } = await createQuiz({
            variables: {
                input: {
                    audience: quizForm.audience,
                    date: formatDateAsAnniversary({ dateTime: quizForm.date, useNewFormat: true }),
                    description: quizForm.description,
                    image: quizForm.image,
                    imageUrl: quizForm.imageUrl,
                    questions: quizForm.questions,
                    title: quizForm.title,
                },
            },
        });

        if (data) {
            const {
                createQuiz: { quiz, errors },
            } = data;
            if (quiz) {
                toast.success('Le quiz a bien été créé !');
                navigate(`/quiz/${quiz._id}`);
            } else {
                if (errors) {
                    errors.forEach((error) => {
                        toast.error(error, { autoClose: false });
                    });

                    if (errors.length > 1) {
                        toast.info('Cliquez pour fermer toutes les notifications', {
                            autoClose: false,
                            onClick: () => toast.dismiss(),
                        });
                    }
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    }

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/quizzes' }],
        },
    ];

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.quizCreate} />
                </HeaderTitle>
                <ActionsAndSave
                    actionGroups={actionGroups}
                    onSave={onSubmit}
                    saveLoading={creationLoading}
                    saveTitle="Créer"
                    saveDataTest="create-button"
                />
            </Header>
            <Content>
                <ScrollableContent>
                    <QuizForm quizForm={quizForm} setQuizForm={setQuizForm} />
                </ScrollableContent>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
